import React, { useMemo } from 'react';
import PropTypes, { bool, func, number, string } from 'prop-types';
import CheckedRadio from '../../../../resources/images/checked-square.svg';
import UncheckedRadio from '../../../../resources/images/unchecked-square.svg';
import classes from './checkbox.css';

const Checkbox = ({
    id,
    isChecked,
    customClassName,
    onClick,
    label,
    dataTestId,
    customAriaLabel,
    cardStyle,
    isFilters,
    tabIndex,
    disabled = false,
    tooltipContent = '',
    tooltipId = ''
}) => {
    const handleCheckboxClick = () => {
        if (!disabled) {
            onClick(label, !isChecked, id);
        }
    };

    const handleKeyDown = event => {
        if (!disabled && (event.key === ' ' || event.key === 'Enter')) {
            event.preventDefault();
            handleCheckboxClick();
        }
    };

    const renderContent = () => {
        if (isFilters) {
            return cardStyle();
        } else {
            // Handle the case when label is an object
            if (typeof label === 'object' && Object.keys(label).length === 0) {
                return null; // or some default content
            }

            return label;
        }
    };

    const checkboxStyle = useMemo(() => {
        return {
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
        };
    }, [disabled]);

    return (
        <div className={`${classes.checkboxWrapper} ${customClassName && customClassName}`}>
            <div
                role="checkbox"
                onClick={handleCheckboxClick}
                onKeyDown={handleKeyDown}
                data-testid={dataTestId}
                data-tooltip-content={tooltipContent}
                data-tooltip-id={tooltipId}
                aria-disabled={disabled}
                aria-checked={isChecked ? true : false}
                aria-label={customAriaLabel ? customAriaLabel : label}
                className={classes.checkboxSVG}
                tabIndex={disabled ? -1 : tabIndex}
                style={checkboxStyle}
            >
                {isChecked ? (
                    <CheckedRadio tabIndex={'-1'} aria-hidden={true} />
                ) : (
                    <UncheckedRadio tabIndex={'-1'} style={{ ...(disabled && { outline: 'none' }) }} aria-hidden={true} />
                )}
            </div>
            {renderContent()}
        </div>
    );
};

Checkbox.propTypes = {
    isChecked: bool,
    onClick: func,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    customClassName: string,
    dataTestId: string,
    customAriaLabel: string,
    id: string,
    tabIndex: number,
    disabled: bool,
    tooltipContent: string,
    tooltipId: string,
};

Checkbox.defaultProps = {
    isChecked: false,
    onClick: () => {},
    customClassName: '',
    dataTestId: '',
    label: '' || {},
    customAriaLabel: '',
    id: '',
    tabIndex: 0,
    disabled: false,
    tooltipContent: '',
    tooltipId: ''
};

export default React.memo(Checkbox);
